import { getLogger } from './logger';
import { SessionConfig, SessionOverrides, SessionResponse, SessionResult } from './types';

const logger = getLogger('API');

/**
 * Starts a session with SirmaAI audio agent
 *
 * @param config - Configuration object containing API key, base URL, and agent ID
 * @returns Promise that resolves to session credentials or rejects with an error
 */
export async function startSession(
  config: SessionConfig,
  sessionOverrides?: SessionOverrides
): Promise<SessionResult> {
  const { apiKey, baseUrl, agentId } = config;

  logger.info('startSession called', {
    agentId,
    baseUrl: baseUrl ? '[REDACTED]' : null,
    hasApiKey: !!apiKey,
  });

  logger.debug('Validating session configuration');
  // Validate required fields
  if (!apiKey || !baseUrl || !agentId) {
    logger.error('Missing required configuration', {
      hasApiKey: !!apiKey,
      hasBaseUrl: !!baseUrl,
      hasAgentId: !!agentId,
    });
    throw new Error('Missing required configuration: apiKey, baseUrl, and agentId are required');
  }

  // Ensure baseUrl doesn't end with slash for consistent URL construction
  const normalizedBaseUrl = baseUrl.replace(/\/$/, '');
  const url = `${normalizedBaseUrl}/client/api/v1/audio-agents/${agentId}/start-session`;

  logger.info('Preparing API request', {
    url: url.replace(/\/[^/]+$/, '/[ENDPOINT]'), // Hide sensitive parts
    agentId,
  });
  logger.debug('Request details', {
    method: 'POST',
    hasApiKey: !!apiKey,
    headers: {
      'Content-Type': 'application/json',
      'X-API-Key': '[REDACTED]',
    },
  });

  const startTime = Date.now();

  try {
    logger.debug('Sending fetch request', { hasSessionOverrides: !!sessionOverrides });
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      // Only send a body when per-session overrides are provided (e.g. the
      // visual-bridge cid instructions); otherwise keep the original bodyless
      // request so existing behavior is unchanged.
      ...(sessionOverrides ? { body: JSON.stringify({ sessionOverrides }) } : {}),
    });

    const responseTime = Date.now() - startTime;
    logger.info('API response received', {
      status: response.status,
      statusText: response.statusText,
      responseTime: `${responseTime}ms`,
      headers: {
        'content-type': response.headers.get('content-type'),
        'content-length': response.headers.get('content-length'),
      },
    });

    if (!response.ok) {
      // Handle HTTP errors
      logger.warn('API request failed, parsing error response');
      const errorData = await response.json().catch(() => {
        logger.debug('Failed to parse error response as JSON');
        return {};
      });

      logger.error('API request failed', {
        status: response.status,
        statusText: response.statusText,
        errorData,
        responseTime: `${responseTime}ms`,
      });
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    logger.debug('Parsing successful API response');
    const apiResponse: SessionResponse = await response.json();

    logger.debug('API response parsed', {
      success: apiResponse.success,
      message: apiResponse.message,
      hasData: !!apiResponse.data,
      timestamp: apiResponse.timestamp,
      version: apiResponse.version,
    });

    if (!apiResponse.success) {
      logger.error('API returned unsuccessful response', {
        message: apiResponse.message,
        apiResponse,
        responseTime: `${responseTime}ms`,
      });
      throw new Error(apiResponse.message || 'Failed to start session');
    }

    logger.debug('Validating response data');
    // Extract and validate required fields from response data
    const { token, wsUrl } = apiResponse.data;

    if (!token || !wsUrl) {
      logger.error('Invalid response: missing required fields', {
        hasToken: !!token,
        hasWsUrl: !!wsUrl,
        responseData: apiResponse.data,
      });
      throw new Error('Invalid response: missing required token or wsUrl fields');
    }

    logger.info('Session started successfully', {
      agentId,
      responseTime: `${responseTime}ms`,
      hasToken: !!token,
      hasWsUrl: !!wsUrl,
    });
    return { token, wsUrl };
  } catch (error) {
    const responseTime = Date.now() - startTime;
    logger.error('Failed to start session', {
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      responseTime: `${responseTime}ms`,
      agentId,
    });

    // Re-throw with more descriptive error message
    if (error instanceof Error) {
      throw new Error(`Failed to start session: ${error.message}`);
    }
    throw new Error('An unexpected error occurred while starting the session');
  }
}
